import { Icon } from '@iconify/react';
import { useState, useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { Menu, MenuItem, IconButton } from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';

import { useBoolean } from 'src/hooks/use-boolean';

// ----------------------------------------------------------------------

export function RHFCheckbox({ name, helperText, label, slotProps, ...other }) {
  const { control } = useFormContext();

  const ariaLabel = `Checkbox ${name}`;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Box sx={slotProps?.wrap}>
          <FormControlLabel
            control={
              <Checkbox
                {...field}
                checked={field.value}
                {...slotProps?.checkbox}
                inputProps={{
                  ...(!label && { 'aria-label': ariaLabel }),
                  ...slotProps?.checkbox?.inputProps,
                }}
              />
            }
            label={label}
            {...other}
          />

          {(!!error || helperText) && (
            <FormHelperText error={!!error} {...slotProps?.formHelperText}>
              {error ? error?.message : helperText}
            </FormHelperText>
          )}
        </Box>
      )}
    />
  );
}

// ----------------------------------------------------------------------

export function RHFMultiCheckbox({ name, label, options, slotProps, helperText, onEdit, onDelete, ...other }) {
  const { control } = useFormContext();

  const getSelected = (selectedItems, item) =>
    selectedItems.includes(item)
      ? selectedItems.filter((value) => value !== item)
      : [...selectedItems, item];

  const accessibility = (val) => val;
  const ariaLabel = (val) => `Checkbox ${val}`;

  const [menuAnchor, setMenuAnchor] = useState({ anchorEl: null, value: null });
  const moreActionsMenu = useBoolean();

  const handleMoreActionsClick = useCallback((event, checkBoxValue) => {
    setMenuAnchor({ anchorEl: event.currentTarget, value: checkBoxValue });
    moreActionsMenu.onTrue();
  }, [moreActionsMenu])

  const handleCloseMenu = useCallback(() => {
    setMenuAnchor({ anchorEl: null, value: null });
    moreActionsMenu.onFalse();
  }, [moreActionsMenu])


  return (
    <>
      <Menu
        anchorEl={menuAnchor.anchorEl}
        open={moreActionsMenu.value}
        onClose={handleCloseMenu}
        slotProps={{
          paper: {
            style: {
              width: '12ch',
            },
          },
        }}
      >
        <MenuItem onClick={() => { onEdit(menuAnchor.value); handleCloseMenu(); }}>Edit </MenuItem>
        <MenuItem onClick={() => { onDelete(menuAnchor.value); handleCloseMenu(); }}>Delete </MenuItem>
      </Menu>

      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <FormControl component="fieldset" sx={slotProps?.wrap}>
            {label && (
              <FormLabel
                component="legend"
                {...slotProps?.formLabel}
                sx={{ mb: 1, typography: 'body2', ...slotProps?.formLabel?.sx }}
              >
                {label}
              </FormLabel>
            )}

            <FormGroup {...other}>
              {options.map((option, index) => (
                <Box key={index} display='flex' justifyContent='space-between'>
                  <FormControlLabel
                    control={

                      <Checkbox
                        checked={field.value.includes(option.value)}
                        onChange={() => field.onChange(getSelected(field.value, option.value))}
                        name={accessibility(option.label)}
                        {...slotProps?.checkbox}
                        inputProps={{
                          ...(!option.label && { 'aria-label': ariaLabel(option.label) }),
                          ...slotProps?.checkbox?.inputProps,
                        }}
                      />
                    }
                    label={option.label}
                  />
                  {option.showMenu &&
                    <IconButton
                      key={index}
                      size="small"
                      aria-label="more actions"
                      onClick={(event) => handleMoreActionsClick(event, option.value)}
                    >
                      <Icon icon="mdi:dots-vertical" style={{ fontSize: 20 }} />
                    </IconButton>
                  }
                </Box>
              ))}
            </FormGroup>
            {(!!error || helperText) && (
              <FormHelperText error={!!error} sx={{ mx: 0 }} {...slotProps?.formHelperText}>
                {error ? error?.message : helperText}
              </FormHelperText>
            )}
          </FormControl>
        )}
      />
    </>
  );
}
